import { useMemo, useState } from "react";
import Drawer from "react-modern-drawer";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { FilterSection } from "shared/components/FilterSection";
import { UnitCurrentStatus, UnitsSource } from "api/units";
import { useUnitsFilterOptions } from "./controller";
import {
  countActiveUnitsFilters,
  EMPTY_UNITS_FILTERS,
  UnitsFilters
} from "./types";

const toggle = <T,>(values: T[], value: T) =>
  values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];

const asStrings = (values: number[]) => values.map(String);

const NEEDS_LOCKBOX_OPTION = "needs_lockbox";

const UnitsFiltersDrawerBody = ({
  open,
  onClose,
  filters,
  onApply,
  liveOnListings
}: {
  open: boolean;
  onClose: () => void;
  filters: UnitsFilters;
  onApply: (filters: UnitsFilters) => void;
  liveOnListings: boolean;
}) => {
  const [draft, setDraft] = useState<UnitsFilters>(filters);
  const { filterOptions, isFetching, setSearch } = useUnitsFilterOptions({
    enabled: open,
    liveOnListings,
    filters
  });

  const hasSelection = useMemo(
    () => countActiveUnitsFilters(draft) > 0,
    [draft]
  );

  return (
    <div className="flex h-full flex-col px-6">
      <div className="flex items-center justify-between border-b border-alpha-40 py-4">
        <h1 className="text-xl font-bold leading-8 text-content-1000">
          Filters
        </h1>
        <IconButton
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.LG}
          onClick={onClose}
          HugeIcon={Cancel01Icon}
        />
      </div>

      <Show if={hasSelection}>
        <div className="pt-4">
          <Button
            variant={ButtonVariant.TEXT}
            className="!px-0"
            onClick={() => setDraft(EMPTY_UNITS_FILTERS)}
          >
            Unselect all
          </Button>
        </div>
      </Show>

      <div className="flex-1 divide-y divide-alpha-40 overflow-y-auto pb-6 pt-4">
        <FilterSection
          title="State"
          isLoading={isFetching}
          optionLimit={4}
          onSearchChange={setSearch}
          options={(filterOptions?.states ?? []).map((bucket) => ({
            id: bucket.value,
            name: bucket.value,
            count: bucket.count
          }))}
          selectedOptions={draft.states}
          onChange={(value) =>
            setDraft((prev) => ({
              ...prev,
              states: toggle(prev.states, value)
            }))
          }
        />

        <FilterSection
          title="City"
          isLoading={isFetching}
          optionLimit={4}
          onSearchChange={setSearch}
          options={(filterOptions?.cities ?? []).map((bucket) => ({
            id: bucket.value,
            name: bucket.value,
            count: bucket.count
          }))}
          selectedOptions={draft.cities}
          onChange={(value) =>
            setDraft((prev) => ({
              ...prev,
              cities: toggle(prev.cities, value)
            }))
          }
        />

        <FilterSection
          title="Property group"
          isLoading={isFetching}
          optionLimit={5}
          onSearchChange={setSearch}
          options={(filterOptions?.property_groups ?? []).map((bucket) => ({
            id: String(bucket.id),
            name: bucket.name ?? `Group ${bucket.id}`,
            count: bucket.count
          }))}
          selectedOptions={asStrings(draft.property_group_ids)}
          onChange={(value) =>
            setDraft((prev) => ({
              ...prev,
              property_group_ids: toggle(prev.property_group_ids, Number(value))
            }))
          }
        />

        <FilterSection
          title="Property"
          isLoading={isFetching}
          optionLimit={5}
          onSearchChange={setSearch}
          options={(filterOptions?.properties ?? []).map((bucket) => ({
            id: String(bucket.id),
            name: bucket.name ?? `Property ${bucket.id}`,
            count: bucket.count
          }))}
          selectedOptions={asStrings(draft.property_ids)}
          onChange={(value) =>
            setDraft((prev) => ({
              ...prev,
              property_ids: toggle(prev.property_ids, Number(value))
            }))
          }
        />

        <FilterSection
          title="Unit"
          isLoading={isFetching}
          optionLimit={5}
          onSearchChange={setSearch}
          options={(filterOptions?.units ?? []).map((unit) => ({
            id: String(unit.id),
            name: unit.title ?? `Unit ${unit.id}`
          }))}
          selectedOptions={asStrings(draft.unit_ids)}
          onChange={(value) =>
            setDraft((prev) => ({
              ...prev,
              unit_ids: toggle(prev.unit_ids, Number(value))
            }))
          }
        />

        <FilterSection
          title="Current status"
          showSearch={false}
          options={(filterOptions?.current_statuses ?? []).map((option) => ({
            id: option.value,
            name: option.label
          }))}
          selectedOptions={draft.current_statuses}
          onChange={(value) =>
            setDraft((prev) => ({
              ...prev,
              current_statuses: toggle(
                prev.current_statuses,
                value as UnitCurrentStatus
              )
            }))
          }
        />

        <FilterSection
          title="Owner"
          isLoading={isFetching}
          optionLimit={5}
          onSearchChange={setSearch}
          options={(filterOptions?.owners ?? []).map((owner) => ({
            id: String(owner.id),
            name: owner.name ?? `Owner ${owner.id}`
          }))}
          selectedOptions={asStrings(draft.owner_ids)}
          onChange={(value) =>
            setDraft((prev) => ({
              ...prev,
              owner_ids: toggle(prev.owner_ids, Number(value))
            }))
          }
        />

        <FilterSection
          title="Source"
          showSearch={false}
          options={(filterOptions?.sources ?? []).map((option) => ({
            id: option.value,
            name: option.label
          }))}
          selectedOptions={draft.sources}
          onChange={(value) =>
            setDraft((prev) => ({
              ...prev,
              sources: toggle(prev.sources, value as UnitsSource)
            }))
          }
        />

        <FilterSection
          title="Lockbox"
          showSearch={false}
          options={(filterOptions?.lockbox ?? []).map((option) => ({
            id: option.value,
            name: option.label,
            count: option.count
          }))}
          selectedOptions={draft.needs_lockbox ? [NEEDS_LOCKBOX_OPTION] : []}
          onChange={() =>
            setDraft((prev) => ({
              ...prev,
              needs_lockbox: !prev.needs_lockbox
            }))
          }
        />
      </div>

      <div className="flex gap-2 border-t border-alpha-100 py-4">
        <Button className="w-[152px]" size={ButtonSize.BASE} onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="w-[152px]"
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          onClick={() => {
            onApply(draft);
            onClose();
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

export const UnitsFiltersDrawer = ({
  open,
  onClose,
  filters,
  onApply,
  liveOnListings
}: {
  open: boolean;
  onClose: () => void;
  filters: UnitsFilters;
  onApply: (filters: UnitsFilters) => void;
  liveOnListings: boolean;
}) => (
  <Drawer
    className="rounded-l-lg"
    direction="right"
    open={open}
    onClose={onClose}
    size={360}
  >
    <UnitsFiltersDrawerBody
      key={String(open)}
      open={open}
      onClose={onClose}
      filters={filters}
      onApply={onApply}
      liveOnListings={liveOnListings}
    />
  </Drawer>
);
